/**
 * WCAG 2.x relative-luminance / contrast-ratio utilities (02-design-system.md §2.3, §7).
 *
 * Pure functions — used by the runtime primary application (applyTheme.ts) and
 * by the automated token-contrast tests. No DOM access.
 */

/** Ink primitive — the dark foreground candidate for bright primaries. */
export const INK_HEX = '#0E1526';
export const WHITE_HEX = '#FFFFFF';
/** Locked default brand (02-design-system.md §2.1). */
export const DEFAULT_PRIMARY = '#00B8DB';

/** Normal-text AA minimum (WCAG 1.4.3). */
export const MIN_TEXT_CONTRAST = 4.5;
/** Non-text (boundary/focus) minimum (WCAG 1.4.11). */
export const MIN_BOUNDARY_CONTRAST = 3;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Validates and normalizes a CSS hex color (`#RGB` or `#RRGGBB`) to uppercase
 * `#RRGGBB`. Returns `null` for anything else — never throws on user input.
 */
export function normalizeHex(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const value = input.trim();
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
  if (!match) return null;
  const raw = match[1] ?? '';
  const six =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  return `#${six.toUpperCase()}`;
}

/** Parses a normalized (or normalizable) hex color to 0–255 channels. */
export function hexToRgb(hex: string): Rgb | null {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function linearizeChannel(channel255: number): number {
  const c = channel255 / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance of an sRGB color, 0 (black) … 1 (white). */
export function relativeLuminance(color: string | Rgb): number {
  const rgb = typeof color === 'string' ? hexToRgb(color) : color;
  if (!rgb) return 0;
  return (
    0.2126 * linearizeChannel(rgb.r) +
    0.7152 * linearizeChannel(rgb.g) +
    0.0722 * linearizeChannel(rgb.b)
  );
}

/** WCAG contrast ratio between two opaque colors, 1 … 21. */
export function contrastRatio(a: string | Rgb, b: string | Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Chooses the foreground for text ON a primary-colored fill: white when white
 * reaches 4.5:1, else ink when ink reaches 4.5:1, else `null` (no compliant
 * pair exists — caller must fall back to the default brand).
 */
export function pickPrimaryForeground(primaryHex: string): string | null {
  if (contrastRatio(WHITE_HEX, primaryHex) >= MIN_TEXT_CONTRAST) return WHITE_HEX;
  if (contrastRatio(INK_HEX, primaryHex) >= MIN_TEXT_CONTRAST) return INK_HEX;
  return null;
}

/** sRGB mix of `color` toward `toward` by `amount` (0–1). Approximation of the CSS ramp. */
export function mixHex(color: string, toward: string, amount: number): string {
  const a = hexToRgb(color);
  const b = hexToRgb(toward);
  if (!a || !b) return color;
  const t = Math.max(0, Math.min(1, amount));
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
}

/**
 * Derives a text-safe shade of `color` reaching `min` contrast on `surface`
 * by mixing toward black or white (whichever direction suits the surface).
 * Used for primary-colored TEXT on cards (links, selected segments), where the
 * raw org color may not reach 4.5:1. Returns a hex; converges because the mix
 * endpoints are black/white.
 */
export function textSafeShade(color: string, surface: string, min = MIN_TEXT_CONTRAST): string {
  const start = normalizeHex(color) ?? DEFAULT_PRIMARY;
  if (contrastRatio(start, surface) >= min) return start;
  const toward = relativeLuminance(surface) > 0.5 ? '#000000' : '#FFFFFF';
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mixHex(start, toward, step / 20);
    if (contrastRatio(candidate, surface) >= min) return candidate;
  }
  return toward;
}
